import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  ASSETS_LAST_MODIFIED_KEY,
  SIMULATE_LDAP_GROUPS_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
} from '../types/constants';
import { simulatedLdapToHeaders, storedToLdapGroups } from './ldapGroups';

/**
 * Get API base URL based on environment
 * Uses beta stage as default if no override is provided
 */
export const getApiBaseUrl = (): string => {
  const apiBaseUrl = "https://prod.na.api.navigator.whs.amazon.dev";
  const stage = "prod".toLowerCase();
  
  // Use localhost when running locally AND webpack config has localhost:8080
  // Check if apiBaseUrl contains localhost:8080 OR if it's still a template (not replaced)
  // which would mean we're in development mode
  if (window.location.hostname === 'localhost' && 
      (apiBaseUrl.includes('localhost:8080') || apiBaseUrl.includes('https://prod.na.api.navigator.whs.amazon.dev'))) {
    return 'http://localhost:8080';
  }
  
  // Otherwise use stage-specific URL
  return `https://alb.${stage}.na.navigator-api.whs.amazon.dev`;
};


/**
 * Create an axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check API health and connectivity
 * @returns Promise that resolves with connection status
 */
export const checkApiHealth = async (): Promise<{status: string, message?: string}> => {
  try {
    const response = await apiClient.get('/healthcheck', { timeout: 5000 });
    
    if (response.status === 200) {
      return { status: 'connected', message: 'API connection successful' };
    } else {
      return { status: 'error', message: `Unexpected response: ${response.status}` };
    }
  } catch (error) {
    let errorMessage = 'API connection failed';
    
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Connection timeout - server is not responding';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Connection error - server may be down or unreachable';
      } else if (error.response) {
        errorMessage = `Server error: ${error.response.status}`;
      }
    }
    
    return { status: 'error', message: errorMessage };
  }
};

/**
 * Add request interceptor to include authentication token and other headers
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Get token from localStorage
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    // Add headers to the request
    config.headers.set({
      // Add authorization header if token exists
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      // Add unique request ID for tracking
      clientRequestId: uuidv4(),
      // Add last modified timestamp for asset cache busting
      [ASSETS_LAST_MODIFIED_KEY]: localStorage.getItem(ASSETS_LAST_MODIFIED_KEY) || 'Not set',
      ...simulatedLdapToHeaders(
        storedToLdapGroups(localStorage.getItem(SIMULATE_LDAP_GROUPS_STORAGE_KEY)),
      ),
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Add response interceptor to handle common error scenarios
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Extract useful error information
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase() || 'GET';
    const errorDetail = error.response?.data?.message || error.message || 'Unknown error';
    
    // Format error message
    const errorMessage = `${method} ${url} failed: ${status} - ${errorDetail}`;
    
    if (error.response) {
      // Handle different error status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          // You might want to redirect to login page or trigger auth flow here
          break;
        case 403:
          // Forbidden - user doesn't have permission
          console.error('Forbidden access', error);
          break;
        default:
          console.error('API Error', errorMessage, error);
      }
    } else if (error.request) {
      // Request was made but no response was received
      console.error('No response received', error.request);
    } else {
      // Something happened in setting up the request
      console.error('Request setup error', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
